import React, { useEffect, useState } from 'react';
import { listFilesUploaded, downloadFileTenants, deleteFileTenant } from '../../../services';
import accessLocalStorage from '../../../services/accessLocalStorage';
import '../../../libs/bulma.min.css';
import './AudioList.css';

import Loading from '../../Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPlay, faStop, faTrash, faMusic } from '@fortawesome/free-solid-svg-icons';

export default function AudioManagerInline() {
  const [data, setData] = useState([]);
  const [message, setMessage] = useState("");
  const [playingItem, setPlayingItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiBaseUrl, setApiBaseUrl] = useState("");

  useEffect(() => {
    const loadBaseUrl = async () => {
      const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
      const ipClean = ipRequest.split('/')[2];
      setApiBaseUrl(`//${ipClean}`);
    };
    loadBaseUrl();
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setMessage("");
    try {
      const result = await listFilesUploaded();
      setLoading(false);

      if (!result || result.error) {
        setMessage(result?.message || "Erro ao buscar lista de arquivos.");
        return;
      }

      const tempData = [];
      for (const [tenantKey, foldersObj] of Object.entries(result)) {
        for (const [folderName, filesArr] of Object.entries(foldersObj)) {
          filesArr.forEach((fileName) => {
            tempData.push({ tenant: tenantKey, folder: folderName, fileName });
          });
        }
      }

      // Ordenar por nome de arquivo
      tempData.sort((a, b) => a.fileName.localeCompare(b.fileName));
      setData(tempData);
    } catch (err) {
      console.error(err);
      setMessage("Erro ao buscar lista de arquivos.");
    }
  };

  const handleDelete = async (item) => {
    const { tenant, folder, fileName } = item;
    setMessage("");
    try {
      const response = await deleteFileTenant({ tenant, type: folder, fileName });
      if (response.status === 200) {
        setMessage("Arquivo deletado com sucesso.");
        fetchAll();
      } else {
        setMessage(response.data?.message || "Erro ao deletar arquivo.");
      }
    } catch (err) {
      console.error(err);
      setMessage("Erro ao deletar arquivo.");
    }
  };

  const handleDownload = async (item) => {
    const { tenant, folder, fileName } = item;
    setMessage("");
    try {
      const response = await downloadFileTenants({ tenant, type: folder, fileName });
      if (response.status !== 200) {
        setMessage(response.data?.message || "Erro ao fazer download.");
        return;
      }
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(link);
    } catch (err) {
      console.error(err);
      setMessage("Erro ao fazer download.");
    }
  };

  const handlePlay = (item) => {
    if (playingItem && playingItem.fileName === item.fileName) {
      setPlayingItem(null);
    } else {
      setPlayingItem(item);
    }
  };

  const getPlayUrl = (item) => {
    const { tenant, folder, fileName } = item;
    return `${apiBaseUrl}/api/uploads/play?tenant=${tenant}&type=${folder}&fileName=${fileName}`;
  };

  return (
    <div className="audio-list-container">
      {loading ? (
        <Loading />
      ) : (
        <div>
          <div className="audio-list-header">
            <div className="audio-list-title-wrapper">
              <svg className="audio-list-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
              <div>
                <h1 className="audio-list-title">Lista de Áudios</h1>
                <p className="audio-list-subtitle">Gerencie arquivos de áudio (URA, MOH, Mensagens)</p>
              </div>
            </div>
          </div>

          {message && (
            <div className="error-message">{message}</div>
          )}

          <div className="audio-table-wrapper">
            <table className="audio-table">
              <thead className="audio-table-header">
                <tr>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th>Arquivo</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => {
                  const isPlaying = playingItem && playingItem.fileName === item.fileName;
                  return (
                    <tr key={index}>
                      <td>{item.tenant}</td>
                      <td>{item.folder}</td>
                      <td>{item.fileName}</td>
                      <td>
                        <div className="audio-actions">
                          <button
                            className="audio-action-btn btn-delete-audio"
                            onClick={() => handleDelete(item)}
                            title="Deletar"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                          <button
                            className="audio-action-btn btn-play-audio"
                            onClick={() => handlePlay(item)}
                            title={isPlaying ? "Parar" : "Reproduzir"}
                          >
                            <FontAwesomeIcon icon={isPlaying ? faStop : faPlay} />
                          </button>
                          <button
                            className="audio-action-btn btn-download-audio"
                            onClick={() => handleDownload(item)}
                            title="Download"
                          >
                            <FontAwesomeIcon icon={faDownload} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data.map((item, index) => {
            const isPlaying = playingItem && playingItem.fileName === item.fileName;
            if (!isPlaying) return null;

            const handleAudioEnded = () => setPlayingItem(null);

            return (
              <div key={index} className="audio-player-card">
                <audio
                  controls
                  autoPlay
                  style={{ width: "100%" }}
                  src={getPlayUrl(item)}
                  onEnded={handleAudioEnded}
                >
                  Seu navegador não suporta o elemento <code>áudio</code>.
                </audio>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
